class SearchForm extends HTMLElement {
  constructor() {
    super();

    this.fetchSearchResults = this.fetchSearchResults.bind(this);
    this.displayResults = this.displayResults.bind(this);
    this.debouncedFetch = debounce(this.fetchSearchResults, 300);

    // helpers to toggle body class safely across multiple search-form instances
    this.addBodyClass = () => document.body.classList.add('predictive-open');
    this.removeBodyClassIfNoOpeners = () => {
      // if no search-form still showing results, remove the class
      const anyOpen = Array.from(document.querySelectorAll('search-form .search-form__results'))
        .some(el => el && el.innerHTML.trim().length > 0);
      if (!anyOpen) document.body.classList.remove('predictive-open');
    };
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.search-form__results');
    if (!this.input || !this.results) return;

    // Input typing
    this.input.addEventListener('input', (event) => {
      const query = event.target.value;
      this.debouncedFetch(query, this.results);
    });

    // Open on focus if input already has value (UX nicety)
    this.input.addEventListener('focus', () => {
      if (this.input.value && this.results.innerHTML.trim()) {
        this.addBodyClass();
      }
    });

    // Close on Escape
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.clearResults();
      }
    });

    // Close when focus leaves the whole component
    this.addEventListener('focusout', (event) => {
      // If focus is moving outside this <search-form>, clear
      if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
        // small delay to allow clicks on results links
        setTimeout(() => {
          this.clearResults();
        }, 150);
      }
    });

    // Close on outside click
    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) {
        this.clearResults();
      }
    }, { capture: true });
  }

  disconnectedCallback() {
    // best-effort cleanup of body class if this instance was the last open one
    this.removeBodyClassIfNoOpeners();
  }

  async fetchSearchResults(query, resultsEl) {
    if (!query) {
      this.clearResults();
      return;
    }

    const searchUrl = `/search/suggest?q=${encodeURIComponent(query)}&resources[type]=product&section_id=header-predictive-search`;
    try {
      const response = await fetch(searchUrl);
      const data = await response.text();
      this.displayResults(data, resultsEl);
    } catch (error) {
      // On error, ensure we don't leave the UI "open"
      this.clearResults();
    }
  }

  displayResults(data, resultsEl) {
    const container = new DOMParser().parseFromString(data, 'text/html')
      .querySelector('#header-predictive-search-results');

    const resultsMarkup = container ? container.innerHTML : '';
    resultsEl.innerHTML = resultsMarkup;

    if (resultsMarkup && resultsMarkup.trim().length > 0) {
      this.addBodyClass();
    } else {
      this.removeBodyClassIfNoOpeners();
    }
  }

  clearResults() {
    if (!this.results) return;
    this.results.innerHTML = '';
    // don’t nuke input immediately if you prefer; leaving as your original behavior:
    if (this.input) {
      // slight delay keeps the field usable if user clicked a result
      setTimeout(() => { this.input.value = ''; }, 0);
    }
    this.removeBodyClassIfNoOpeners();
  }
}

customElements.define('search-form', SearchForm);
